/**
 * Drizzle query builders + query keys for COHORTS and PROGRAMS (F2-A).
 *
 * Local-first consumption over the per-role streams: my_cohorts_student /
 * my_cohorts_teacher / campus_admin_scope / org_admin_scope / academy_org
 * (programs and open cohorts). Writes go through the write-path or the
 * REST endpoints of /api/cohorts (server-side activation/capacity/waitlist).
 */

import {
  and,
  asc,
  cohort,
  cohort_enrollment,
  cohort_group,
  cohort_group_member,
  cohort_module_setting,
  desc,
  eq,
  isNull,
  type PowerSyncDrizzleDB,
  program,
  program_course,
} from "@space/powersync";
import type { components } from "@space/client";

export const cohortQueryKeys = {
  all: ["academy-cohorts"] as const,
  byOrg: (orgId: string | undefined) => ["academy-cohorts", "org", orgId] as const,
  byId: (cohortId: string | undefined) => ["academy-cohorts", "detail", cohortId] as const,
  openByCourse: (orgId: string | undefined, courseId: string | undefined) =>
    ["academy-cohorts", "open", orgId, courseId] as const,
  myEnrollments: (userId: string | undefined) =>
    ["academy-cohorts", "my-enrollments", userId] as const,
  // Per-role nav: do I have any cohort_teacher row of my own? (assigned teacher).
  myTeaching: (userId: string | undefined) => ["academy-cohorts", "my-teaching", userId] as const,
  roster: (cohortId: string | undefined) => ["academy-cohorts", "roster", cohortId] as const,
  /** 1C: PUBLIC profiles of a cohort's teachers (classroom cover). */
  teacherProfiles: (cohortId: string | undefined) =>
    ["academy-cohorts", "teacher-profiles", cohortId] as const,
  /**
   * Plan 06 · E3: identities of the cohort's people. SINGLE and only possible
   * source — the local mirror of `"user"` disappeared with the stream
   * trimming, so there are no longer two sources to split by `mode`.
   */
  identities: (cohortId: string | undefined) =>
    ["academy-cohorts", "identities", cohortId] as const,
  groups: (cohortId: string | undefined) => ["academy-cohorts", "groups", cohortId] as const,
  groupMembers: (cohortId: string | undefined) =>
    ["academy-cohorts", "group-members", cohortId] as const,
  moduleSettings: (cohortId: string | undefined) =>
    ["academy-cohorts", "module-settings", cohortId] as const,
  /**
   * Internal academy catalog. Plan 06 · E1: there is ONE single source,
   * `GET /api/academy/:orgId/courses`. The `orgCourses` key (local mirror over
   * `course_organization`) disappeared with the `org_courses` stream.
   */
  orgCoursesRest: (orgId: string | undefined) =>
    ["academy-cohorts", "org-courses-rest", orgId] as const,
  programs: (orgId: string | undefined) => ["academy-programs", "org", orgId] as const,
  programById: (programId: string | undefined) =>
    ["academy-programs", "detail", programId] as const,
  programCourses: (programId: string | undefined) =>
    ["academy-programs", "courses", programId] as const,
};

// ─── Cohorts ────────────────────────────────────────────────────────────────

/** Cohorts of an organization (admin/teacher view — what the stream synced). */
export function getCohortsByOrgQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(cohort)
    .where(and(eq(cohort.organization_id, orgId), isNull(cohort.deleted_at)))
    .orderBy(desc(cohort.created_at));
}

/** A cohort by id. */
export function getCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db.select().from(cohort).where(eq(cohort.id, cohortId)).limit(1);
}

/** OPEN cohorts of a course in my org (enrollment selection in the catalog). */
export function getOpenCohortsByCourseQuery(
  db: PowerSyncDrizzleDB,
  orgId: string,
  courseId: string,
) {
  return db
    .select()
    .from(cohort)
    .where(
      and(
        eq(cohort.organization_id, orgId),
        eq(cohort.course_id, courseId),
        eq(cohort.status, "open"),
        isNull(cohort.deleted_at),
      ),
    )
    .orderBy(asc(cohort.start_date));
}

/** MY cohort enrollments (private, my_cohorts_student stream). */
export function getMyCohortEnrollmentsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(cohort_enrollment)
    .where(and(eq(cohort_enrollment.user_id, userId), isNull(cohort_enrollment.deleted_at)))
    .orderBy(desc(cohort_enrollment.created_at));
}

/**
 * Roster of a cohort: **enrollment only**.
 *
 * **D3 (plan 06 · E3):** the `leftJoin(user, …)` that used to bring name/email/
 * image has been REMOVED. Identity has a single source on the client
 * (`useCohortIdentities`, which requests it from `GET /api/cohorts/:id/roster`,
 * the only place where the email is gated by capability) and gets attached
 * with `attachIdentity`. The `cohort_enrollment` columns stay: they keep
 * arriving via sync because the teacher writes them offline in the classroom.
 */
export function getCohortRosterQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select({
      id: cohort_enrollment.id,
      user_id: cohort_enrollment.user_id,
      status: cohort_enrollment.status,
      waitlist_position: cohort_enrollment.waitlist_position,
      enrolled_at: cohort_enrollment.enrolled_at,
      completed_at: cohort_enrollment.completed_at,
      withdrawn_at: cohort_enrollment.withdrawn_at,
      final_grade: cohort_enrollment.final_grade,
      notes: cohort_enrollment.notes,
    })
    .from(cohort_enrollment)
    .where(and(eq(cohort_enrollment.cohort_id, cohortId), isNull(cohort_enrollment.deleted_at)))
    .orderBy(asc(cohort_enrollment.created_at));
}

// E4 (plan 06) · REMOVED: `getCohortTeacherProfilesQuery` used to live here,
// the `innerJoin(cohort_teacher, teacher_profile)` that painted the classroom
// cover. It relied on `academy_org` replicating ALL of the academy's public
// profiles to EVERY member just to show the two or three of one classroom —
// the same "replicate a thousand to teach three" pattern that E4 closes.
// It's replaced by `GET /api/cohorts/:id/teachers`, which returns exactly
// that cohort's teachers and still doesn't touch the person's identity
// (name and email come from the roster, which doesn't answer to a student).

/**
 * Public profile of a cohort teacher (no personal identity).
 *
 * E4: it's DERIVED from the server DTO instead of hand-declared. The change
 * isn't cosmetic — `specialties` used to be `string | null` because SQLite
 * stored the serialized `jsonb` and every consumer re-parsed it; now it
 * arrives already as an array and the parsing disappears.
 */
export type CohortTeacherProfileRow = components["schemas"]["CohortTeacherCardDTO"];

/** Roster row WITHOUT identity (what comes out of the local query). */
export interface CohortRosterRow {
  id: string;
  user_id: string | null;
  status: string | null;
  waitlist_position: number | null;
  enrolled_at: string | null;
  completed_at: string | null;
  withdrawn_at: string | null;
  final_grade: number | null;
  notes: string | null;
}

// D3 (plan 06 · E3): `getCohortTeachersQuery` and `CohortTeacherRow` have been
// REMOVED. They were the second identity join —over `cohort_teacher`— and its
// `useCohortTeachers` hook had NOT A SINGLE consumer: no screen paints the
// cohort's teacher list today. It's removed entirely instead of being
// migrated; keeping a query alive that nobody calls is exactly the debt the
// policy forbids. When a screen needs it, the cohort's teachers already
// arrive via `GET /api/cohorts/:id/roster`.

/** Active groups of a cohort. */
export function getCohortGroupsQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(cohort_group)
    .where(and(eq(cohort_group.cohort_id, cohortId), isNull(cohort_group.deleted_at)))
    .orderBy(asc(cohort_group.name));
}

/** Members of all of a cohort's groups. */
export function getCohortGroupMembersQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(cohort_group_member)
    .where(eq(cohort_group_member.cohort_id, cohortId))
    .orderBy(asc(cohort_group_member.created_at));
}

/** Per-module settings (due/open/visibility) of a cohort. */
export function getCohortModuleSettingsQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(cohort_module_setting)
    .where(eq(cohort_module_setting.cohort_id, cohortId));
}

// ─── Organization courses (cohort/program picker) ────────────────
//
// D2 · REMOVED: `getOrgCoursesQuery` (local join over `course_organization`)
// and its `OrgCourseRow` row used to live here. They read the replica that
// fed the `org_courses` stream, now removed: the table no longer travels to
// the client, so the query would always return empty.
//
// The academy's internal catalog is requested from `GET /api/academy/:orgId/courses`
// (`fetchOrgCourses`), which filters publication and deletion and requires
// `ManageAcademics` for drafts. The row shape is now declared by the only
// consumer (`useOrgCourses`), derived from the generated DTO — not a
// hand-written interface imitating the dead query.

// ─── Programs ───────────────────────────────────────────────────────────────

/** Active programs of an organization. */
export function getProgramsByOrgQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(program)
    .where(and(eq(program.organization_id, orgId), isNull(program.deleted_at)))
    .orderBy(asc(program.name));
}

/** A program by id. */
export function getProgramQuery(db: PowerSyncDrizzleDB, programId: string) {
  return db.select().from(program).where(eq(program.id, programId)).limit(1);
}

/** Courses of a program, ordered. */
export function getProgramCoursesQuery(db: PowerSyncDrizzleDB, programId: string) {
  return db
    .select()
    .from(program_course)
    .where(eq(program_course.program_id, programId))
    .orderBy(asc(program_course.position));
}
